/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { getLocations } from '../api';

const BACKEND = process.env.REACT_APP_API_BASE;

const TRIAL_DAYS = 14;

const LOCATION_TYPES = ['Cafe', 'Restaurant', 'Shop', 'Hotel', 'Office', 'Other'];

// ─── Onboarding flow (new users with no screens) ─────────────────────────────
function Onboarding({ user, onComplete }) {
  const [step, setStep] = useState(1);
  const [screenCount, setScreenCount] = useState(1);
  const [billing, setBilling] = useState('yearly');
  const [locName, setLocName] = useState('');
  const [locType, setLocType] = useState('Cafe');
  const [screens, setScreens] = useState([{ token: '', name: '' }]);
  const [errors, setErrors] = useState({});
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    setScreens(Array.from({ length: screenCount }, (_, i) => screens[i] || { token: '', name: '' }));
  }, [screenCount]);

  const updateScreen = (idx, field, val) => {
    const updated = [...screens];
    updated[idx] = { ...updated[idx], [field]: val };
    setScreens(updated);
  };

  const validateStep2 = () => {
    const errs = {};
    if (!locName.trim()) errs.locName = 'Location name is required';
    screens.forEach((scr, i) => {
      if (!scr.name.trim()) errs[`name_${i}`] = 'Screen name is required';
      if (!scr.token.trim()) errs[`token_${i}`] = 'Token is required';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCheckout = async () => {
  setCheckoutLoading(true);
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${BACKEND}/stripe/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ screen_count: screenCount, locName, locType, screens, billing, trial_period_days: TRIAL_DAYS })
    });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  } catch (e) { console.error(e); }
  setCheckoutLoading(false);
};

  // Step 1: Screen count
  if (step === 1) return (
    <div style={{ padding: 16, maxWidth: 560 }}>
      <div style={stepHeader}>
        <div style={stepBadge}>1 of 3</div>
        <h2 style={stepTitle}>How many screens?</h2>
        <p style={stepSub}>Select the number of screens you want to set up. You can add more later.</p>
      </div>

      <div style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '2rem', marginBottom: 20, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 20 }}>
          <button onClick={() => setScreenCount(Math.max(1, screenCount - 1))}
            style={counterBtn}>−</button>
          <div>
            <div style={{ fontSize: 56, fontWeight: 700, color: '#111', lineHeight: 1 }}>{screenCount}</div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>screen{screenCount !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={() => setScreenCount(Math.min(20, screenCount + 1))}
            style={counterBtn}>+</button>
        </div>

        <div style={{ background: '#F5EFE6', borderRadius: 10, padding: '14px 20px', display: 'inline-block' }}>
          <div style={{ background: '#1565C0', color: '#fff', borderRadius: 8, padding: '10px 16px', marginBottom: 12, fontSize: 13, fontWeight: 700, maxWidth: 320 }}>
            🎉 Start with a {TRIAL_DAYS}-day free trial
            <div style={{ fontWeight: 400, fontSize: 12, opacity: 0.85, marginTop: 3 }}>Set up everything now. Pay only when your trial ends.</div>
          </div>

          {/* Billing toggle */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, justifyContent: 'center' }}>
            <button onClick={() => setBilling('monthly')}
              style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid #E0D8CF', cursor: 'pointer', fontSize: 12, fontWeight: billing === 'monthly' ? 600 : 400, background: billing === 'monthly' ? '#111' : '#fff', color: billing === 'monthly' ? '#fff' : '#555' }}>
              Monthly
            </button>
            <button onClick={() => setBilling('yearly')}
              style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid #E0D8CF', cursor: 'pointer', fontSize: 12, fontWeight: billing === 'yearly' ? 600 : 400, background: billing === 'yearly' ? '#111' : '#fff', color: billing === 'yearly' ? '#fff' : '#555' }}>
              Yearly
            </button>
          </div>
          <div style={{ background: '#F5EFE6', borderRadius: 10, padding: '14px 20px', display: 'inline-block', marginBottom: 8 }}>
            {billing === 'yearly' ? (
              <>
                <span style={{ fontSize: 13, color: '#777' }}>{screenCount} screen{screenCount !== 1 ? 's' : ''} × £70/year = </span>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#8B1E2D' }}>£{screenCount * 70}/year</span>
              </>
            ) : (
              <>
                <span style={{ fontSize: 13, color: '#777' }}>{screenCount} screen{screenCount !== 1 ? 's' : ''} × £7/month = </span>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#8B1E2D' }}>£{screenCount * 7}/month</span>
              </>
            )}
          </div>
          {billing === 'yearly' && <div style={{ fontSize: 11, color: '#2D6A4F', fontWeight: 600 }}>Save £{screenCount * (7*12 - 70)} vs monthly</div>}
        </div>

        <div style={{ fontSize: 12, color: '#aaa', marginTop: 10 }}>20+ screens? Contact us for enterprise pricing.</div>
      </div>

      <button onClick={() => setStep(2)} style={primaryBtn}>
        Continue →
      </button>
    </div>
  );

  // Step 2: Location + screen details
  if (step === 2) return (
    <div style={{ padding: 16, maxWidth: 600 }}>
      <div style={stepHeader}>
        <div style={stepBadge}>2 of 3</div>
        <h2 style={stepTitle}>Set up your screens</h2>
        <p style={stepSub}>Enter your location details and the token shown on each screen device.</p>
      </div>

      {/* Location */}
      <div style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1.25rem', marginBottom: 16 }}>
        <div style={sectionLabel}>Location</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 180 }}>
            <label style={lbl}>Location name *</label>
            <input value={locName} onChange={e => setLocName(e.target.value)} placeholder="e.g. Cafe Novo — Kadıköy" style={{ ...inp, borderColor: errors.locName ? '#e00' : '#E0D8CF' }} />
            {errors.locName && <div style={errTxt}>{errors.locName}</div>}
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label style={lbl}>Type</label>
            <select value={locType} onChange={e => setLocType(e.target.value)} style={inp}>
              {LOCATION_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Screens */}
      {screens.map((scr, i) => (
        <div key={i} style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1.25rem', marginBottom: 12 }}>
          <div style={{ ...sectionLabel, marginBottom: 12 }}>Screen {i + 1}</div>

          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Screen name *</label>
            <input value={scr.name} onChange={e => updateScreen(i, 'name', e.target.value)}
              placeholder="e.g. Main Display, Counter Screen"
              style={{ ...inp, borderColor: errors[`name_${i}`] ? '#e00' : '#E0D8CF' }} />
            {errors[`name_${i}`] && <div style={errTxt}>{errors[`name_${i}`]}</div>}
          </div>

          <div>
            <label style={lbl}>Device token *</label>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
              Power on your screen device — the token appears at the bottom of the activation screen.
            </div>
            <input value={scr.token} onChange={e => updateScreen(i, 'token', e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              style={{ ...inp, fontFamily: 'monospace', fontSize: 12, borderColor: errors[`token_${i}`] ? '#e00' : '#E0D8CF' }} />
            {errors[`token_${i}`] && <div style={errTxt}>{errors[`token_${i}`]}</div>}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button onClick={() => setStep(1)} style={secondaryBtn}>← Back</button>
        <button onClick={() => { if (validateStep2()) setStep(3); }} style={primaryBtn}>
          Review & Pay →
        </button>
      </div>
    </div>
  );

  // Step 3: Summary + payment
  if (step === 3) return (
    <div style={{ padding: 16, maxWidth: 560 }}>
      <div style={stepHeader}>
        <div style={stepBadge}>3 of 3</div>
        <h2 style={stepTitle}>Review & Start Trial</h2>
        <p style={stepSub}>Confirm your setup. You'll begin with a free {TRIAL_DAYS}-day trial — no charge until it ends.</p>
      </div>

      {/* Summary */}
      <div style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1.25rem', marginBottom: 16 }}>
        <div style={sectionLabel}>Summary</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F0EBE6', fontSize: 14 }}>
          <span style={{ color: '#777' }}>Location</span>
          <span style={{ fontWeight: 500 }}>{locName} <span style={{ fontSize: 11, color: '#aaa' }}>({locType})</span></span>
        </div>
        {screens.map((scr, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F0EBE6', fontSize: 14 }}>
            <span style={{ color: '#777' }}>Screen {i + 1}</span>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 500 }}>{scr.name}</div>
              <div style={{ fontSize: 11, color: '#aaa', fontFamily: 'monospace' }}>{scr.token.substring(0, 18)}...</div>
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0', fontSize: 15, fontWeight: 700 }}>
          <span>Total</span>
          <span style={{ color: '#8B1E2D' }}>{billing === 'yearly' ? `£${screenCount * 70}/year` : `£${screenCount * 7}/month`}</span>
        </div>
        <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Trial included · {billing === 'yearly' ? 'billed annually (£5/screen/month)' : 'billed monthly'}</div>
      </div>

      <div style={{ background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: 12, padding: '1rem', marginBottom: 20, fontSize: 13, color: '#1565C0' }}>
        Your screens will be activated immediately and the {TRIAL_DAYS}-day free trial starts now. When it ends, you'll be billed {billing === 'yearly' ? `£${screenCount * 70}/year` : `£${screenCount * 7}/month`} automatically unless you cancel.
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => setStep(2)} style={secondaryBtn}>← Back</button>
        <button onClick={handleCheckout} disabled={checkoutLoading} style={{ ...primaryBtn, flex: 1 }}>
          {checkoutLoading ? 'Redirecting to payment...' : `Start ${TRIAL_DAYS}-day Free Trial →`}
        </button>
      </div>
    </div>
  );

  return null;
}

// ─── Existing user account page ───────────────────────────────────────────────
function ExistingAccount({ user, locations, totalScreens, onlineScreens }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    first_name: user?.user_metadata?.first_name || '',
    last_name: user?.user_metadata?.last_name || '',
    phone: user?.user_metadata?.phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [addonCount, setAddonCount] = useState(1);
  const [addonBilling, setAddonBilling] = useState('yearly');
  const [addonLoading, setAddonLoading] = useState(false);
  const [paymentMsg] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('payment') || '';
  });

  const annualCost = totalScreens * 70;
  const renewalDate = new Date();
  renewalDate.setFullYear(renewalDate.getFullYear() + 1);
  const meta = user?.user_metadata || {};
  const fullName = meta.full_name || `${meta.first_name || ''} ${meta.last_name || ''}`.trim() || '—';

  const handleSave = async () => {
    setSaving(true);
    await supabase.auth.updateUser({
      data: {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        full_name: `${form.first_name} ${form.last_name}`.trim(),
      }
    });
    setSaving(false);
    setSaved(true);
    setEditMode(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND}/stripe/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) { console.error(e); }
    setPortalLoading(false);
  };

  const handleAddonCheckout = async () => {
    setAddonLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ screen_count: addonCount, billing: addonBilling, addon: true })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) { console.error(e); }
    setAddonLoading(false);
  };

  return (
    <div style={{ padding: 16, maxWidth: 700 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 24 }}>Account</h2>

      {paymentMsg === 'success' && (
        <div style={{ background: '#E6F0EA', color: '#2D6A4F', padding: '12px 16px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          Payment successful! Your screens are now active.
        </div>
      )}
      {saved && (
        <div style={{ background: '#E6F0EA', color: '#2D6A4F', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
          Profile updated successfully.
        </div>
      )}

      {/* Plan */}
      <div style={{ background: '#111', borderRadius: 12, padding: '1.5rem', marginBottom: 16, color: '#fff' }}>
        <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, fontWeight: 600 }}>Current Plan</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{totalScreens} <span style={{ fontSize: 16, fontWeight: 400, color: '#888' }}>screen{totalScreens !== 1 ? 's' : ''}</span></div>
            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{onlineScreens} online now</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#C45C6A' }}>£{annualCost}<span style={{ fontSize: 14, fontWeight: 400, color: '#888' }}>/year</span></div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>£5/screen/month · billed annually</div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid #222', marginTop: 16, paddingTop: 16, display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Renewal</div>
              <div style={{ fontSize: 13, color: '#ccc' }}>{renewalDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>Status</div>
              <span style={{ background: '#1A3D1A', color: '#4CAF50', padding: '2px 10px', borderRadius: 20, fontSize: 11 }}>Active</span>
            </div>
          </div>
          <button onClick={handlePortal} disabled={portalLoading}
            style={{ background: 'none', color: '#888', border: '1px solid #333', borderRadius: 8, padding: '8px 16px', fontSize: 12, cursor: 'pointer' }}>
            {portalLoading ? 'Loading...' : 'Manage subscription'}
          </button>
        </div>
      </div>

      {/* Screens by location */}
      {locations.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1.25rem', marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, fontWeight: 600 }}>Screens by location</div>
          {locations.map(loc => {
            const screens = loc.screens || [];
            const online = screens.filter(s => s.is_online).length;
            return (
              <div key={loc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F0EBE6', fontSize: 14 }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{loc.name}</span>
                  <span style={{ fontSize: 11, background: '#eee', padding: '1px 7px', borderRadius: 20, marginLeft: 8, color: '#666' }}>{loc.type}</span>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: online > 0 ? 'green' : '#999' }}>{online}/{screens.length} online</span>
                  <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>£{screens.length * 70}/yr</span>
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', fontSize: 14, fontWeight: 700 }}>
            <span>Total</span>
            <span style={{ color: '#8B1E2D' }}>£{annualCost}/year</span>
          </div>
        </div>
      )}

      {/* Personal info */}
      <div style={{ background: '#fff', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1.25rem', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Personal Information</div>
          {!editMode && <button onClick={() => setEditMode(true)} style={{ background: 'none', border: '1px solid #E0D8CF', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', color: '#555' }}>Edit</button>}
        </div>
        {!editMode ? (
          <>
            <div style={infoRow}><span style={{ color: '#777' }}>Full name</span><span style={{ fontWeight: 500 }}>{fullName}</span></div>
            <div style={infoRow}><span style={{ color: '#777' }}>Email</span><span style={{ fontWeight: 500 }}>{user?.email}</span></div>
            <div style={{ ...infoRow, border: 'none' }}><span style={{ color: '#777' }}>Phone</span><span style={{ fontWeight: 500 }}>{meta.phone || '—'}</span></div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}><label style={lbl}>First name</label><input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} style={inp} /></div>
              <div style={{ flex: 1 }}><label style={lbl}>Last name</label><input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} style={inp} /></div>
            </div>
            <div style={{ marginBottom: 12 }}><label style={lbl}>Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inp} placeholder="+44 7700 000000" /></div>
            <div style={{ marginBottom: 8 }}>
              <label style={lbl}>Email</label>
              <input value={user?.email} disabled style={{ ...inp, background: '#f5f5f5', color: '#999' }} />
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Email cannot be changed here.</div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={handleSave} disabled={saving} style={{ background: '#8B1E2D', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
              <button onClick={() => setEditMode(false)} style={{ background: 'none', border: '1px solid #E0D8CF', borderRadius: 8, padding: '9px 20px', fontSize: 13, cursor: 'pointer', color: '#555' }}>Cancel</button>
            </div>
          </>
        )}
      </div>

      {/* Add more screens (add-on) */}
      <div style={{ background: '#F5EFE6', border: '1px solid #E0D8CF', borderRadius: 12, padding: '1.25rem', marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: '#8B1E2D', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, marginBottom: 10 }}>Add more screens</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 14, lineHeight: 1.6 }}>
          Need another screen at this location? Add it to your plan — billed on top of your current subscription. No trial on add-ons.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 12 }}>
          <button onClick={() => setAddonCount(Math.max(1, addonCount - 1))} style={counterBtnSmall}>−</button>
          <div style={{ fontSize: 26, fontWeight: 700, minWidth: 36, textAlign: 'center' }}>{addonCount}</div>
          <button onClick={() => setAddonCount(Math.min(20, addonCount + 1))} style={counterBtnSmall}>+</button>
        </div>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 12 }}>
          <button onClick={() => setAddonBilling('monthly')}
            style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid #E0D8CF', cursor: 'pointer', fontSize: 11, fontWeight: addonBilling === 'monthly' ? 600 : 400, background: addonBilling === 'monthly' ? '#111' : '#fff', color: addonBilling === 'monthly' ? '#fff' : '#555' }}>
            Monthly
          </button>
          <button onClick={() => setAddonBilling('yearly')}
            style={{ padding: '5px 12px', borderRadius: 20, border: '1px solid #E0D8CF', cursor: 'pointer', fontSize: 11, fontWeight: addonBilling === 'yearly' ? 600 : 400, background: addonBilling === 'yearly' ? '#111' : '#fff', color: addonBilling === 'yearly' ? '#fff' : '#555' }}>
            Yearly
          </button>
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#8B1E2D', textAlign: 'center', marginBottom: 12 }}>
          +£{addonBilling === 'yearly' ? addonCount * 70 : addonCount * 7}/{addonBilling === 'yearly' ? 'year' : 'month'}
        </div>
        <button onClick={handleAddonCheckout} disabled={addonLoading}
          style={{ width: '100%', background: '#8B1E2D', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 700, cursor: addonLoading ? 'not-allowed' : 'pointer', opacity: addonLoading ? 0.7 : 1 }}>
          {addonLoading ? 'Redirecting to payment...' : 'Buy screens →'}
        </button>
      </div>

      {/* Help */}
      <div style={{ background: '#fff', border: '1px solid #F5C6CB', borderRadius: 12, padding: '1.25rem' }}>
        <div style={{ fontSize: 11, color: '#9B2335', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, marginBottom: 10 }}>Need help?</div>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 10, lineHeight: 1.6 }}>To cancel your plan or discuss enterprise pricing, contact us directly.</div>
        <a href="mailto:okan@biteandbrand.co.uk" style={{ display: 'inline-block', background: '#8B1E2D', color: '#fff', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Contact support</a>
      </div>
    </div>
  );
}

// ─── Main Account component ───────────────────────────────────────────────────
export default function Account() {
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [totalScreens, setTotalScreens] = useState(0);
  const [onlineScreens, setOnlineScreens] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      const res = await getLocations();
      setLocations(res.data || []);
      let screens = 0, online = 0;
      (res.data || []).forEach(loc => {
        (loc.screens || []).forEach(scr => {
          screens++;
          if (scr.is_online) online++;
        });
      });
      setTotalScreens(screens);
      setOnlineScreens(online);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div style={{ padding: 32, color: '#999', fontSize: 14 }}>Loading...</div>;

  // New user (no screens) → show onboarding
  if (totalScreens === 0) {
    return <Onboarding user={user} onComplete={() => window.location.href = '/'} />;
  }

  // Existing user → show account page
  return <ExistingAccount user={user} locations={locations} totalScreens={totalScreens} onlineScreens={onlineScreens} />;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const stepHeader = { marginBottom: 24 };
const stepBadge = { display: 'inline-block', background: '#F5EFE6', color: '#8B1E2D', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, marginBottom: 10, letterSpacing: 1 };
const stepTitle = { fontSize: 22, fontWeight: 700, margin: '0 0 8px', color: '#111' };
const stepSub = { fontSize: 14, color: '#777', margin: 0, lineHeight: 1.6 };
const sectionLabel = { fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, marginBottom: 14 };
const counterBtn = { width: 44, height: 44, borderRadius: 8, border: '1px solid #E0D8CF', background: '#fff', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const counterBtnSmall = { width: 34, height: 34, borderRadius: 8, border: '1px solid #E0D8CF', background: '#fff', fontSize: 17, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const primaryBtn = { background: '#8B1E2D', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const secondaryBtn = { background: 'none', color: '#555', border: '1px solid #E0D8CF', borderRadius: 8, padding: '12px 20px', fontSize: 14, cursor: 'pointer' };
const lbl = { fontSize: 12, color: '#555', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 12px', border: '1px solid #E0D8CF', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const errTxt = { fontSize: 11, color: '#c00', marginTop: 4 };
const infoRow = { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F0EBE6', fontSize: 14 };